import React, { useCallback, useEffect, useState } from "react";
import { connect } from "react-redux";

import Tooltip from "antd/es/tooltip";
import cn from "classnames";
import { useIntl } from "react-intl";

import Color from "@mapmycustomers/shared/enum/Color";
import EntityType from "@mapmycustomers/shared/enum/EntityType";
import {
  EntitiesSupportedByEmailFeature,
  EntityTypesSupportedByEmailFeature,
  MapEntity,
} from "@mapmycustomers/shared/types/entity";
import { stopEvents } from "@mapmycustomers/shared/util/browser";
import { convertGeoPointToLatLngLiteral } from "@mapmycustomers/shared/util/geo/GeoService";
import useBoolean from "@mapmycustomers/shared/util/hook/useBoolean";
import { Avatar, Button, EntityTypeShapedIcon } from "@mapmycustomers/ui";

import FieldGrid from "@app/component/FieldGrid";
import LoadingDots from "@app/component/LoadingDots";
import Interactive from "@app/component/map/FeaturedMap/essentials/controls/Interactive";
import EntityActivities from "@app/component/RecordCard/components/EntityActivities";
import EntityAddress from "@app/component/RecordCard/components/EntityAddress";
import RecordActionsBar from "@app/component/RecordCard/components/RecordActionsBar";
import UserPreviewCard from "@app/component/UserPreviewCard";
import { showEntityView } from "@app/store/entityView/actions";
import RecordPreviewConfigurationColumns from "@app/store/recordPreview/RecordPreviewConfigurationColumns";
import { getRecordPreviewConfigurationColumns } from "@app/store/recordPreview/selectors";
import { RootState } from "@app/store/rootReducer";
import anyColorToHex from "@app/util/colors/anyColorToHex";
import useAnalytics from "@app/util/contexts/useAnalytics";
import getFieldModelByEntityType from "@app/util/fieldModel/getByEntityType";
import { PersonFieldName } from "@app/util/fieldModel/PersonFieldModel";
import { getEntityTypeAnalyticsName } from "@app/util/ui";

import Overlay from "./components/Overlay";
import styles from "./EntityAnnotation.module.scss";
import { fetchAnnotationData, subscribeEntityEmail, unsubscribeEntityEmail } from "./store/actions";

export interface EntityAnnotationProps {
  actions?: React.ReactElement;
  coordinates?: { left: number; top: number };
  defaultEntity?: MapEntity; // in case entity is known
  entityId: MapEntity["id"];
  entityType: MapEntity["entity"];
  forceShowEngagement?: boolean;
  map?: google.maps.Map;
  onHide: () => void;
  showArrow?: boolean;
  showGenericActions?: boolean;
  zIndex?: number;
}

interface Props extends EntityAnnotationProps {
  columns: RecordPreviewConfigurationColumns;
  onEmailSubscribe: typeof subscribeEntityEmail;
  onEmailUnsubscribe: typeof unsubscribeEntityEmail;
  onFetchData: typeof fetchAnnotationData;
  onShowEntityView: typeof showEntityView;
}

const EntityAnnotation: React.FC<Props> = ({
  actions,
  columns,
  coordinates,
  defaultEntity,
  entityId,
  entityType,
  forceShowEngagement,
  map,
  onEmailSubscribe,
  onEmailUnsubscribe,
  onFetchData,
  onHide,
  onShowEntityView,
  showArrow,
  showGenericActions,
  zIndex = 1000, // guaranteed to override sidebar and navbar
}) => {
  const intl = useIntl();
  const analytics = useAnalytics();

  const [loading, startLoading, stopLoading] = useBoolean();
  const [entity, setEntity] = useState<MapEntity | undefined>(defaultEntity);
  const [emailSubscribed, setEmailSubscribed] = useState<boolean | undefined>(undefined);
  const [addNoteModalVisible, setNodeModalVisibility] = useState<boolean>(false);

  useEffect(() => {
    if (defaultEntity) {
      return;
    }

    startLoading();
    onFetchData({
      callback: ({ emailSubscribed, entity }) => {
        setEntity(entity);
        setEmailSubscribed(emailSubscribed);
        stopLoading();
      },
      entityId,
      entityType,
      failureCallback: stopLoading,
    });
  }, [defaultEntity, entityId, entityType, onFetchData, startLoading, stopLoading]);

  const geoPoint =
    entity?.entity === EntityType.DEAL
      ? entity?.account?.geoPoint ?? entity?.contact?.geoPoint
      : entity?.geoPoint;
  const position = geoPoint ? convertGeoPointToLatLngLiteral(geoPoint) : undefined;

  const handleShowDetails = useCallback(() => {
    if (entity) {
      onShowEntityView({
        entityId: entity.id,
        entityType: entity.entity,
      });
      analytics.clicked([
        `${getEntityTypeAnalyticsName(entity.entity, false, false)} Pin`,
        "Annotation",
      ]);
    }
  }, [analytics, entity, onShowEntityView]);

  const color: string = anyColorToHex(
    (entity?.entity === EntityType.DEAL ? entity?.stage.color : entity?.color) ?? Color.BLACK
  );
  const isVisible = !!entity && ((!!map && !!position) || !!coordinates);
  const showActions = showGenericActions && columns.actions;
  const showEngagement = columns.engagement || forceShowEngagement;

  const fieldModel = getFieldModelByEntityType(entityType);
  const nameField = fieldModel?.getByName(
    entityType === EntityType.PERSON ? PersonFieldName.FIRST_NAME : "name"
  );
  const addressField = fieldModel?.getByName("address");

  const handleEmailSubscribe = useCallback(() => {
    onEmailSubscribe({
      callback: () => setEmailSubscribed(true),
      entityId: entityId as EntitiesSupportedByEmailFeature["id"],
      entityType: entityType as EntityTypesSupportedByEmailFeature,
    });
  }, [entityId, entityType, setEmailSubscribed, onEmailSubscribe]);

  const handleEmailUnsubscribe = useCallback(() => {
    onEmailUnsubscribe({
      callback: () => setEmailSubscribed(false),
      entityId: entityId as EntitiesSupportedByEmailFeature["id"],
      entityType: entityType as EntityTypesSupportedByEmailFeature,
    });
  }, [entityId, entityType, setEmailSubscribed, onEmailUnsubscribe]);

  return isVisible && (coordinates || position) ? (
    <Overlay
      coordinates={coordinates}
      map={map}
      onClickOutside={onHide}
      position={position}
      usedInModal={!addNoteModalVisible}
      zIndex={zIndex}
    >
      <div className={cn(styles.container, { [styles.offset]: !coordinates })}>
        {showArrow && <div className={styles.arrow} style={{ borderRightColor: color }} />}
        <div className={styles.border} style={{ borderColor: color }} />
        <Interactive>
          <div className={styles.body} onClick={handleShowDetails}>
            <div className={styles.header}>
              <div className={styles.name}>
                <Tooltip
                  title={intl.formatMessage({
                    id: "entityAnnotation.title.hiddenNameTooltip",
                    defaultMessage: "Protected Information",
                    description:
                      "Protected information tooltip in Entity Annotation title when the name field is hidden",
                  })}
                  trigger={!nameField?.isReadable ? ["hover"] : []}
                >
                  <Button className={styles.nameBtn} onClick={handleShowDetails} type="link">
                    {entity.name}
                  </Button>
                </Tooltip>
              </div>

              <div className={styles.ownerIcon}>
                {entity.user ? (
                  <UserPreviewCard userId={entity.user.id}>
                    <Avatar actionOnHover onClick={stopEvents} user={entity.user} />
                  </UserPreviewCard>
                ) : (
                  <EntityTypeShapedIcon className={styles.entityIcon} entityType={entity.entity} />
                )}
              </div>
            </div>

            <div className={styles.content}>
              <div className={styles.contentHeader}>
                {addressField?.isReadable && <EntityAddress entity={entity} />}
                {showEngagement && (
                  <EntityActivities emailSubscribed={emailSubscribed} entity={entity} />
                )}
              </div>

              <FieldGrid
                className={styles.fieldGrid}
                columns={columns[entity.entity]}
                entity={entity}
                showEmpty={!loading}
              />

              {loading && <LoadingDots mini />}
            </div>
          </div>

          <RecordActionsBar
            className={styles.actionBar}
            emailSubscribed={emailSubscribed}
            enableGeneric={showActions}
            entity={entity}
            extraActions={actions}
            onAddNoteVisibilityChange={setNodeModalVisibility}
            onEdit={handleShowDetails}
            onEmailSubscribe={handleEmailSubscribe}
            onEmailUnsubscribe={handleEmailUnsubscribe}
          />
        </Interactive>
      </div>
    </Overlay>
  ) : null;
};

const mapStateToProps = (state: RootState) => ({
  columns: getRecordPreviewConfigurationColumns(state),
});

const mapDispatchToProps = {
  onEmailSubscribe: subscribeEntityEmail,
  onEmailUnsubscribe: unsubscribeEntityEmail,
  onFetchData: fetchAnnotationData,
  onShowEntityView: showEntityView,
};

export default connect(mapStateToProps, mapDispatchToProps)(EntityAnnotation);
